import React, {useState, useEffect} from "react";
import {LoadingButton, Box, Stack, Text} from "@unibuddy/patron";
import {useTranslation} from "@unibuddy/intl";
import {useLocation} from "react-router-dom";
import {IconChevronDown} from "@unibuddy/icons";
import styled from "styled-components";
import {ApolloError} from "@apollo/client";

import {ContentCard} from "../../Mentor/components/ContentSection/ContentCard";
import {IntlNamespaces} from "../../../../translations/types";
import {AllPostsField, BlogPostField} from "../../../../types/generated";
import {BLOG_POSTS_LIMIT_WIDGET} from "../constants";
import {INTL_VIEW_MORE_CONTENT} from "../../../../translations/common";
import {INTL_BLOGS_COMING_SOON, INTL_NO_BLOGS_MESSAGE} from "../../../../translations/blogs";

type FetchMoreVariables = {
    offset: number;
    limit: number;
};

type UpdateQueryReturn = {
    allPostsInfo: {
        blogPosts: BlogPostField[];
        blogPostsCount: number;
    };
};

type FetchMoreFunction = ({
    variables,
    updateQuery,
}: {
    variables: FetchMoreVariables;
    updateQuery: (
        prev: UpdateQueryReturn,
        {fetchMoreResult}: {fetchMoreResult: UpdateQueryReturn | null},
    ) => UpdateQueryReturn;
}) => Promise<{data: UpdateQueryReturn}>;

type ContentCardListContainerProps = {
    data?: {allPostsInfo: AllPostsField};
    loading: boolean;
    error?: ApolloError | undefined;
    fetchMore: FetchMoreFunction;
};

const StyledIconChevronDown = styled(IconChevronDown)`
    margin: 0 !important;
    margin-left: 4px !important;
`;

const BlogPreviewCardsGrid = styled.div`
    display: grid;
    width: 100%;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
    grid-template-columns: repeat(1, minmax(0, 1fr));

    @media (min-width: 375px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (min-width: 632px) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
`;

export function ContentCardListContainer({
    data,
    loading,
    error,
    fetchMore,
}: ContentCardListContainerProps) {
    const {pathname} = useLocation();
    const {t} = useTranslation([IntlNamespaces.COMMON, IntlNamespaces.BLOGS]);
    const [showLoadMoreButton, setShowLoadMoreButton] = useState<boolean>(false);
    const previousPostCount = React.useRef<number>(0);

    useEffect(() => {
        // Avoid setting the button state if it hasn't changed
        const currentPostCount = data?.allPostsInfo?.blogPosts.length || 0;

        if (previousPostCount.current !== currentPostCount) {
            previousPostCount.current = currentPostCount;
            setShowLoadMoreButton(currentPostCount < data?.allPostsInfo?.blogPostsCount);
        }
    }, [data]); // Only run this effect when `data` changes

    if (loading) {
        return <>Loading...</>;
    }

    if (error) {
        console.error(error);
        return <>Error: Something went wrong, please try again later!</>;
    }

    if (!data?.allPostsInfo?.blogPosts.length) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                marginTop="medium"
                gap="medium"
            >
                <Text>
                    {t(
                        `${INTL_BLOGS_COMING_SOON.namespace}:${INTL_BLOGS_COMING_SOON.key}`,
                        INTL_BLOGS_COMING_SOON.defaultValue,
                    )}
                </Text>
                <Text>
                    {t(
                        `${INTL_NO_BLOGS_MESSAGE.namespace}:${INTL_NO_BLOGS_MESSAGE.key}`,
                        INTL_NO_BLOGS_MESSAGE.defaultValue,
                    )}
                </Text>
            </Box>
        );
    }

    return (
        <Stack space="medium" align="center">
            <BlogPreviewCardsGrid>
                {data?.allPostsInfo?.blogPosts.map((post: BlogPostField, index: number) => {
                    return (
                        <ContentCard
                            key={`content-card-${post.id}`}
                            post={post}
                            contentUrl={pathname}
                            position={index}
                            height="365px"
                        />
                    );
                })}
            </BlogPreviewCardsGrid>
            {showLoadMoreButton && (
                <LoadingButton
                    loading={loading}
                    round
                    color="university"
                    onClick={() => {
                        fetchMore({
                            variables: {
                                offset: data.allPostsInfo.blogPosts.length,
                                limit: BLOG_POSTS_LIMIT_WIDGET,
                            },
                            updateQuery: (
                                prev: {
                                    allPostsInfo: {
                                        blogPosts: BlogPostField[];
                                        blogPostsCount: number;
                                    };
                                },
                                {fetchMoreResult},
                            ) => {
                                if (!fetchMoreResult) return prev;

                                const prevPosts = prev.allPostsInfo.blogPosts;
                                const fetchMorePosts = fetchMoreResult.allPostsInfo.blogPosts;
                                const allPosts = [...prevPosts, ...fetchMorePosts];

                                return {
                                    allPostsInfo: {
                                        ...prev.allPostsInfo,
                                        blogPosts: allPosts,
                                    },
                                };
                            },
                        });
                    }}
                >
                    {t(INTL_VIEW_MORE_CONTENT.key, INTL_VIEW_MORE_CONTENT.defaultValue)}
                    <StyledIconChevronDown width="16px" height="16px" color="inherit" />
                </LoadingButton>
            )}
        </Stack>
    );
}
